import { usePublicChatContext } from "@lazarentertainment/public-chat-web/dist/src/react"
import { Close } from "@lazarentertainment/web-design-system/components/iconOutline"
import { useLayoutContext } from "client/components/layout"
import { Button } from "client/designSystem/Button"
import { Sheet } from "client/designSystem/Sheet"
import {
  Plugin,
  useGlobalContext,
} from "client/packages/utils/GlobalContext"
import { useCallback, useMemo } from "react"

import { useGiveawaysContext } from "../../context/GiveawaysContext"

import EntryDrawnMessage from "./components/EntryDrawnMessage"
import EntryForm, { type EntryFormData } from "./components/EntryForm"
import EntrySubmittedMessage from "./components/EntrySubmittedMessage"

import styles from "./GiveawaysMobileSheet.module.css"

/**
 * Represents the different stages of the giveaway entry form flow.
 */
enum EntryState {
  NOT_ENTERED,
  ENTRY_SUBMITTED,
  ENTRY_DRAWN,
}

/**
 * The primary UI component for the giveaways plugin.
 */
export function GiveawaysMobileSheet() {
  /**
   * Context
   */
  const {
    state: { isGiveawaysPluginOpen },
  } = useGlobalContext()
  const { displayName } = usePublicChatContext()
  const { layout } = useLayoutContext()
  const { setPluginOpen } = useGlobalContext()
  const { giveaway, entry, mutations } = useGiveawaysContext()

  /**
   * Mutations
   */
  const [enterGiveaway, { loading: enterGiveawayLoading }] =
    mutations.enterGiveaway

  /**
   * Handlers
   */
  const handleFormSubmit = useCallback(
    (formData: EntryFormData) => {
      if (!giveaway) return

      enterGiveaway({
        variables: {
          giveawayId: giveaway.id,
          entry: {
            ...formData,
            // If available, include the user's chat display name in the entry.
            displayName,
          },
        },
      })
    },
    [enterGiveaway, giveaway, displayName],
  )

  const closeSheet = useCallback(() => {
    setPluginOpen(Plugin.GIVEAWAYS, false)
  }, [setPluginOpen])

  /**
   * Derived state
   */
  const entryState = useMemo((): EntryState => {
    if (!entry) {
      return EntryState.NOT_ENTERED
    } else if (!entry.drawnAt) {
      return EntryState.ENTRY_SUBMITTED
    }
    return EntryState.ENTRY_DRAWN
  }, [entry])

  const sheetTitle = useMemo(() => {
    switch (entryState) {
      case EntryState.NOT_ENTERED:
        return giveaway?.title ?? ""
      case EntryState.ENTRY_SUBMITTED:
        return "You're in!"
      case EntryState.ENTRY_DRAWN:
        return "You won! 🎉"
    }
  }, [entryState, giveaway?.title])

  const sheetDescription = useMemo(() => {
    switch (entryState) {
      case EntryState.NOT_ENTERED:
        return "The following information is required to enter this giveaway."
      default:
        return undefined
    }
  }, [entryState])

  if (!giveaway) {
    return null
  }

  return (
    <Sheet
      open={isGiveawaysPluginOpen}
      data-layout={layout}
      title={sheetTitle}
      description={sheetDescription}
      topRightContent={
        <Button
          variant="tertiary-gray"
          iconOnly
          icon={Close}
          onClick={closeSheet}
          data-robot="giveaways-close"
        />
      }
    >
      <div className={styles.giveawaysMobileSheetContent}>
        {entryState === EntryState.NOT_ENTERED && (
          <EntryForm
            openGiveaway={giveaway}
            onSubmit={handleFormSubmit}
            isLoading={enterGiveawayLoading}
          />
        )}

        {entryState === EntryState.ENTRY_SUBMITTED && (
          <EntrySubmittedMessage onClose={closeSheet} />
        )}

        {entryState === EntryState.ENTRY_DRAWN && (
          <EntryDrawnMessage onClose={closeSheet} />
        )}
      </div>
    </Sheet>
  )
}
